import { MOBILE_BROWSER_RECORDING_ACK_KEY_PREFIX } from "@common/lib/accountScopedStorageKeyPrefixes";
import { isRunningInExtensionIframe } from "./recordingTelemetry";

// EN-1383: phone and tablet browsers keep MediaRecorder running after the screen sleeps but
// feed it silence, so most of a visit can be lost without any error. These helpers decide
// when to warn before a web recording and remember, per user (localStorage), that the user confirmed.

// Region-free on purpose. The id must match canonicalAppStoreID in happy-doc-ios
// HappyDocTests/App/AppStoreLinkTests.swift. The Google Play package com.happydoc.happydoc is the Android app.
export const APP_STORE_URL = "https://apps.apple.com/app/happydoc/id6479448993";
export const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.happydoc.happydoc";

export interface MobileBrowserEnv {
    userAgent: string;
    maxTouchPoints: number;
    inExtensionIframe: boolean;
}

export const readMobileBrowserEnv = (): MobileBrowserEnv => ({
    userAgent: typeof navigator !== "undefined" ? (navigator.userAgent ?? "") : "",
    maxTouchPoints: typeof navigator !== "undefined" ? (navigator.maxTouchPoints ?? 0) : 0,
    inExtensionIframe: isRunningInExtensionIframe(),
});

const isAppleMobile = ({ userAgent, maxTouchPoints }: MobileBrowserEnv): boolean =>
    /iPhone|iPad|iPod/i.test(userAgent) || (/Macintosh/.test(userAgent) && maxTouchPoints > 1);

/**
 * True for phone and tablet browsers, outside the Chrome extension.
 * - Android, iPhone, iPad, iPod user agents.
 * - iPadOS, which sends a Macintosh UA: Macs report 0 touch points, iPads report 5.
 * - Android "Desktop site" mode, which sends `X11; Linux`: every such web UA seen in prod during the
 *   EN-1383 investigation was an Android device, so it also needs touch points. ChromeOS also
 *   sends X11, so it is excluded by its `CrOS` token.
 * Windows is never matched: touch laptops are common and touch hardware is not a phone.
 */
export const isMobileBrowserRecordingDevice = (env: MobileBrowserEnv = readMobileBrowserEnv()): boolean => {
    if (env.inExtensionIframe) return false;
    if (/Android/i.test(env.userAgent)) return true;
    if (isAppleMobile(env)) return true;
    return /X11; Linux/.test(env.userAgent) && !/CrOS/.test(env.userAgent) && env.maxTouchPoints > 0;
};

export const storeUrlForDevice = (env: MobileBrowserEnv = readMobileBrowserEnv()): string =>
    isAppleMobile(env) ? APP_STORE_URL : GOOGLE_PLAY_URL;

// Users whose acknowledgement could not be written to storage (private mode, blocked site data), so
// they see the warning once per page session instead of before every recording.
const acknowledgedThisPageSession = new Set<string>();

// Test-only: production code never calls this.
export const __resetMobileAcknowledgementForTests = (): void => {
    acknowledgedThisPageSession.clear();
};

// Per effective user (the impersonated user when impersonating), so the first vet on a shared clinic
// tablet does not silence the warning for the next one. The prefix is in the account-scoped registry,
// so it also survives sign-out. With no user id nothing is acknowledged or persisted.
const ackKey = (userId: string): string => `${MOBILE_BROWSER_RECORDING_ACK_KEY_PREFIX}${userId}`;

// Storage can throw. A failed read means "not acknowledged" unless this page session already confirmed.
export const hasAcknowledgedMobileBrowserRecording = (userId: string | null): boolean => {
    if (!userId) return false;
    if (acknowledgedThisPageSession.has(userId)) return true;
    try {
        return localStorage.getItem(ackKey(userId)) !== null;
    } catch {
        return false;
    }
};

export const acknowledgeMobileBrowserRecording = (userId: string | null): void => {
    if (!userId) return;
    try {
        localStorage.setItem(ackKey(userId), new Date().toISOString());
    } catch {
        // Not persisted: remember it for this page session only.
        acknowledgedThisPageSession.add(userId);
    }
};
